import {
  TEAM_TRIP_ANSWER_LABELS,
  teamLineupSchema,
  teamTripsSchema,
  type AdminTeam,
  type TeamLineup,
  type TeamTrip,
  type TeamTripAnswer,
  type TeamTrips,
} from '@growup/site-shared';
import {
  Button,
  Card,
  Checkbox,
  DateTimeInput,
  Field,
  Input,
  Notice,
  StatusBadge,
  Textarea,
  type StatusTone,
} from '@growup/ui';
import { useEffect, useState, type FormEvent } from 'react';

import { getJson, messageOf, send } from '../api';
import { isoToLisbonInput, lisbonInputToIso } from '../events/labels';
import { formatTeamEventWhen } from './CalendarCard';

const BASE = '/api/site/teams';

const ANSWER_TONE: Record<TeamTripAnswer, StatusTone> = {
  pending: 'warning',
  going: 'success',
  not_going: 'neutral',
  authorized: 'success',
  refused: 'danger',
};

const today = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Lisbon' }).format(new Date());

/** Quem esta hoje no lineup: so esses se convocam. */
function currentLineup(lineup: TeamLineup) {
  const ended = new Set(lineup.seasons.filter((s) => s.endsOn < today()).map((s) => s.id));
  return lineup.members.filter(
    (m) => m.leftOn === null && (m.seasonId === null || !ended.has(m.seasonId)),
  );
}

export const formatTripWhen = (t: Pick<TeamTrip, 'startsAt' | 'endsAt'>) =>
  `${formatTeamEventWhen(t.startsAt, null)} até ${formatTeamEventWhen(t.endsAt, null)}`;

/**
 * Deslocacoes a torneios (parte 7, docs/equipas, RF-EQ11): convocar quem esta
 * no lineup; os adultos respondem "vou / nao vou" e, pelos jovens, os
 * encarregados autorizam ou recusam. Todos recebem email.
 */
export function TripsCard({ team }: { team: AdminTeam }) {
  const [trips, setTrips] = useState<TeamTrips | null>(null);
  const [lineup, setLineup] = useState<TeamLineup | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const archived = team.status === 'archived';

  useEffect(() => {
    Promise.all([
      getJson(`${BASE}/${team.id}/trips`, teamTripsSchema),
      getJson(`${BASE}/${team.id}/lineup`, teamLineupSchema),
    ])
      .then(([t, l]) => {
        setTrips(t);
        setLineup(l);
      })
      .catch((e: unknown) => setError(messageOf(e)));
  }, [team.id]);

  const run = async (method: string, path: string, body?: unknown): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      setTrips(teamTripsSchema.parse(await send(method, `${BASE}${path}`, body)));
      return true;
    } catch (e) {
      setError(messageOf(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (trips === null || lineup === null) {
    return (
      <Card headingLevel={2} title="Deslocações">
        {error ? <Notice tone="danger">{error}</Notice> : <p>A carregar…</p>}
      </Card>
    );
  }
  const people = currentLineup(lineup);

  return (
    <Card headingLevel={2} title="Deslocações">
      <div className="flex flex-col gap-4">
        <p className="text-gu-ink-muted">
          Convoque quem está no lineup: cada um recebe um email e responde na conta do site. Pelos
          jovens, autoriza ou recusa o encarregado.
        </p>
        {error ? <Notice tone="danger">{error}</Notice> : null}
        {trips.upcoming.length === 0 ? <p>Sem deslocações marcadas.</p> : null}
        <ul className="flex flex-col divide-y divide-gu-line">
          {trips.upcoming.map((t) =>
            editing === t.id ? (
              <li key={t.id} className="py-3">
                <TripForm
                  initial={t}
                  people={people}
                  busy={busy}
                  submitLabel="Guardar"
                  onCancel={() => setEditing(null)}
                  onSubmit={(body) =>
                    run('PUT', `/trips/${t.id}`, body).then((ok) => {
                      if (ok) setEditing(null);
                      return ok;
                    })
                  }
                />
              </li>
            ) : (
              <li key={t.id} className="flex flex-col gap-3 py-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                  <TripSummary trip={t} />
                  {t.cancelled ? null : (
                    <div className="flex flex-wrap justify-end gap-2 sm:ml-auto">
                      <Button size="sm" variant="secondary" onClick={() => setEditing(t.id)}>
                        Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy}
                        onClick={() => {
                          if (
                            window.confirm(
                              'Cancelar a deslocação? Todos os convocados são avisados.',
                            )
                          ) {
                            void run('POST', `/trips/${t.id}/cancel`);
                          }
                        }}
                      >
                        Cancelar
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy}
                        onClick={() => {
                          if (window.confirm('Apagar esta deslocação? Ninguém é avisado.')) {
                            void run('DELETE', `/trips/${t.id}`);
                          }
                        }}
                      >
                        Apagar
                      </Button>
                    </div>
                  )}
                </div>
                <Answers trip={t} />
              </li>
            ),
          )}
        </ul>
        {archived ? null : adding ? (
          <div className="border-2 border-gu-line p-3">
            {people.length === 0 ? (
              <p>O lineup está vazio: não há quem convocar.</p>
            ) : (
              <TripForm
                people={people}
                busy={busy}
                submitLabel="Convocar"
                onCancel={() => setAdding(false)}
                onSubmit={(body) =>
                  run('POST', `/${team.id}/trips`, body).then((ok) => {
                    if (ok) setAdding(false);
                    return ok;
                  })
                }
              />
            )}
          </div>
        ) : (
          <div>
            <Button size="sm" onClick={() => setAdding(true)}>
              Nova deslocação
            </Button>
          </div>
        )}
        {trips.past.length > 0 ? (
          <div className="flex flex-col gap-1">
            <h3 className="font-sub text-label font-bold uppercase text-gu-ink-muted">
              Anteriores
            </h3>
            <ul className="text-gu-ink-muted">
              {trips.past.map((t) => (
                <li key={t.id}>
                  {t.title} · {formatTeamEventWhen(t.startsAt, null)}
                  {t.cancelled ? ' · cancelada' : ` · ${String(t.members.length)} convocados`}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

function TripSummary({ trip: t }: { trip: TeamTrip }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className={t.cancelled ? 'font-semibold line-through' : 'font-semibold'}>
          {t.title}
        </span>
        {t.cancelled ? <StatusBadge tone="danger">Cancelada</StatusBadge> : null}
      </div>
      <p>{formatTripWhen(t)}</p>
      {t.location ? <p className="text-caption text-gu-ink-muted">{t.location}</p> : null}
      {t.details ? (
        <p className="whitespace-pre-line text-caption text-gu-ink-muted">{t.details}</p>
      ) : null}
    </div>
  );
}

function Answers({ trip }: { trip: TeamTrip }) {
  const count = (pred: (a: TeamTripAnswer) => boolean) =>
    trip.members.filter((m) => pred(m.answer)).length;
  const yes = count((a) => a === 'going' || a === 'authorized');
  const no = count((a) => a === 'not_going' || a === 'refused');
  const waiting = count((a) => a === 'pending');
  return (
    <div className="flex flex-col gap-2">
      <p className="font-sub text-label font-bold uppercase text-gu-ink-muted">
        {String(trip.members.length)} convocados · {String(yes)} confirmados · {String(no)} não vão
        {waiting > 0 ? ` · ${String(waiting)} sem resposta` : ''}
      </p>
      <ul className="flex flex-wrap gap-x-4 gap-y-2">
        {trip.members.map((m) => (
          <li key={m.accountId} className="flex items-center gap-2">
            <span>{m.login}</span>
            <StatusBadge tone={ANSWER_TONE[m.answer]}>
              {m.youth && m.answer === 'pending'
                ? 'Falta o encarregado'
                : TEAM_TRIP_ANSWER_LABELS[m.answer]}
            </StatusBadge>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TripForm({
  initial,
  people,
  busy,
  submitLabel,
  onCancel,
  onSubmit,
}: {
  initial?: TeamTrip;
  people: TeamLineup['members'];
  busy: boolean;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (body: unknown) => Promise<boolean>;
}) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [location, setLocation] = useState(initial?.location ?? '');
  const [startsAt, setStartsAt] = useState(isoToLisbonInput(initial?.startsAt ?? null));
  const [endsAt, setEndsAt] = useState(isoToLisbonInput(initial?.endsAt ?? null));
  const [details, setDetails] = useState(initial?.details ?? '');
  // Ao criar, todos os jogadores do lineup vem marcados.
  const [chosen, setChosen] = useState<Set<string>>(
    new Set(
      initial
        ? initial.members.map((m) => m.accountId)
        : people.filter((p) => ['captain', 'player'].includes(p.role)).map((p) => p.accountId),
    ),
  );
  const [fields, setFields] = useState<Record<string, string>>({});

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const problems: Record<string, string> = {};
    const starts = lisbonInputToIso(startsAt);
    const ends = lisbonInputToIso(endsAt);
    if (title.trim() === '') problems.title = 'Indique o torneio.';
    if (starts === null) problems.startsAt = 'Indique a data e a hora.';
    if (ends === null) problems.endsAt = 'Indique a data e a hora.';
    else if (starts && ends <= starts) problems.endsAt = 'O regresso tem de ser depois da partida.';
    if (chosen.size === 0) problems.accountIds = 'Convoque pelo menos uma pessoa.';
    setFields(problems);
    if (Object.keys(problems).length > 0) return;
    void onSubmit({
      title,
      location,
      startsAt: starts,
      endsAt: ends,
      details,
      accountIds: [...chosen],
    });
  };

  return (
    <form className="flex flex-col gap-3" onSubmit={submit} noValidate>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Torneio" error={fields.title}>
          <Input value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field label="Local" optional>
          <Input value={location} maxLength={200} onChange={(e) => setLocation(e.target.value)} />
        </Field>
        <Field label="Partida" hint="Hora de Lisboa." error={fields.startsAt}>
          <DateTimeInput value={startsAt} onChange={setStartsAt} />
        </Field>
        <Field label="Regresso" error={fields.endsAt}>
          <DateTimeInput value={endsAt} onChange={setEndsAt} />
        </Field>
      </div>
      <Field
        label="Informações"
        optional
        hint="Ponto de encontro, transporte, o que levar. Vai no email da convocatória."
      >
        <Textarea
          value={details}
          maxLength={2000}
          rows={3}
          onChange={(e) => setDetails(e.target.value)}
        />
      </Field>
      <fieldset className="flex flex-col gap-2">
        <legend className="font-sub text-label font-bold uppercase text-gu-ink-muted">
          Convocados
        </legend>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {people.map((p) => (
            <Checkbox
              key={p.accountId}
              label={p.youth ? `${p.login} (jovem)` : p.login}
              checked={chosen.has(p.accountId)}
              onChange={(e) => {
                const on = e.currentTarget.checked;
                setChosen((s) => {
                  const next = new Set(s);
                  if (on) next.add(p.accountId);
                  else next.delete(p.accountId);
                  return next;
                });
              }}
            />
          ))}
        </div>
        {fields.accountIds ? (
          <p role="alert" className="text-caption font-semibold text-gu-danger">
            {fields.accountIds}
          </p>
        ) : null}
        {initial ? (
          <p className="text-caption text-gu-ink-muted">
            Quem marcar agora recebe a convocatória; quem desmarcar deixa de estar convocado.
          </p>
        ) : null}
      </fieldset>
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={busy}>
          {submitLabel}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Voltar
        </Button>
      </div>
    </form>
  );
}
